import { Color, Spacing } from '@persona-id/design-tokens';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  CustomListConfigHeaderMapping,
  CustomListConfigMappableField,
} from 'dashboard/models/report-template-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError, { ServerError } from 'lib/errors/ApiResponseError';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';

interface CustomListConfigHeaderMapperProps {
  customListFile: FileWithPreview;
  mappableFields: Array<CustomListConfigMappableField>;
  headerMappings: Array<CustomListConfigHeaderMapping>;
  isHeaderMappingsValidated: boolean;
  validateHeaderMappingsError?: Error | ApiNetworkError | ApiResponseError;
  onHeaderMappingsChange: (value: CustomListConfigHeaderMapping[]) => void;
  handleValidateHeaderMappings: (
    headerMappings: Array<CustomListConfigHeaderMapping>,
    customListFile: FileWithPreview,
  ) => Promise<void>;
  setIsHeaderMappingsValidated: (value: boolean) => void;
}

export const CustomListConfigHeaderMapper = ({
  customListFile,
  mappableFields,
  headerMappings,
  isHeaderMappingsValidated,
  validateHeaderMappingsError,
  onHeaderMappingsChange,
  handleValidateHeaderMappings,
  setIsHeaderMappingsValidated,
}: CustomListConfigHeaderMapperProps) => {
  const { headers, dataPreview } = customListFile;

  const updateHeaderMappings = (mapping: CustomListConfigHeaderMapping) => {
    const prev = headerMappings || [];
    const index = prev.findIndex((field) => field['header-name'] === mapping['header-name']);
    const newMapping = {
      'header-name': mapping['header-name'],
      'mapped-field': mapping['mapped-field'],
    };

    if (index === -1) {
      if (mapping['mapped-field'].length !== 0) {
        onHeaderMappingsChange([...prev, newMapping]);
      }
    } else {
      if (mapping['mapped-field'].length === 0) {
        // Remove the mapping entirely if the user has unselected a field
        const updatedMappings = [...prev.slice(0, index), ...prev.slice(index + 1)];
        onHeaderMappingsChange(updatedMappings);
      } else {
        const updatedMappings = [...prev];
        updatedMappings[index] = newMapping;
        onHeaderMappingsChange(updatedMappings);
      }
    }
  };

  const [headerMappingValidationErrors, setHeaderMappingValidationErrors] = useState<
    ServerError[] | undefined
  >([]);

  useApiErrorHandler({
    error: validateHeaderMappingsError,
    onError: (error) => {
      setHeaderMappingValidationErrors(error?.errors);
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  // Clears validation errors when the file changes (updating the actual headerMappings
  // and isHeaderMappingsValidated is handled in CustomListConfigEditor)
  useEffect(() => {
    setHeaderMappingValidationErrors([]);
  }, [customListFile]);

  return (
    <>
      {headers && headers.length === 0 ? (
        <Paragraph>
          No headers found in your CSV. Please upload a CSV file with headers to continue.
        </Paragraph>
      ) : (
        <>
          <Paragraph color='primary'>
            Map your file to the Persona fields you would like to match against. Then, we will
            validate that the data in your CSV matches the expected data types.
          </Paragraph>
          <Spacer height={2} />
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>CSV header name</TableHeaderCell>
                <TableHeaderCell>Data preview</TableHeaderCell>
                <TableHeaderCell>Mapped field</TableHeaderCell>
                <TableHeaderCell>Accepted type</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {headers &&
                headers.map((header, i) => (
                  <CustomListConfigHeaderMapperRow
                    key={`custom-list-report-template-edit__header-mapping-row-${i}`}
                    rowIndex={i}
                    header={header}
                    dataPreview={(dataPreview && dataPreview[i]) || ''}
                    headerMappings={headerMappings}
                    mappableFields={mappableFields}
                    updateHeaderMappings={updateHeaderMappings}
                    setIsHeaderMappingsValidated={setIsHeaderMappingsValidated}
                    setHeaderMappingValidationErrors={setHeaderMappingValidationErrors}
                  />
                ))}
            </TableBody>
          </Table>
          <Spacer height={2} />
          <FooterRow>
            {isHeaderMappingsValidated && (
              <SuccessMessage>
                <Paragraph color='primary'>Your mappings are valid!</Paragraph>
              </SuccessMessage>
            )}
            {headerMappingValidationErrors && headerMappingValidationErrors.length > 0 && (
              <ErrorMessage>
                <Paragraph color='primary'>
                  The following validation errors were found in your file, based on the mappings you
                  selected:
                </Paragraph>
                <Spacer height={2} />
                <UnorderedList>
                  {headerMappingValidationErrors.map((error, i) => (
                    <li
                      key={`custom-list-report-template-edit__header-mapping-validation-error-${i}`}
                    >
                      {error.title}
                    </li>
                  ))}
                </UnorderedList>
              </ErrorMessage>
            )}
            <Button
              variant='primary'
              id='custom-list-report-template-edit__save-mappings'
              width='button'
              disabled={!headerMappings || headerMappings.length === 0 || isHeaderMappingsValidated}
              onClick={() => {
                setIsHeaderMappingsValidated(false);
                setHeaderMappingValidationErrors([]);
                handleValidateHeaderMappings(headerMappings, customListFile);
              }}
            >
              Validate Mappings
            </Button>
          </FooterRow>
        </>
      )}
    </>
  );
};

interface CustomListConfigHeaderMapperRowProps {
  rowIndex: number;
  header: string;
  dataPreview: string;
  headerMappings: Array<CustomListConfigHeaderMapping>;
  mappableFields: Array<CustomListConfigMappableField>;
  updateHeaderMappings: (mapping: CustomListConfigHeaderMapping) => void;
  setIsHeaderMappingsValidated: (value: boolean) => void;
  setHeaderMappingValidationErrors: (errors: ServerError[] | undefined) => void;
}

const CustomListConfigHeaderMapperRow = ({
  rowIndex,
  header,
  dataPreview,
  headerMappings,
  mappableFields,
  updateHeaderMappings,
  setIsHeaderMappingsValidated,
  setHeaderMappingValidationErrors,
}: CustomListConfigHeaderMapperRowProps) => {
  const [selectedFieldName, setSelectedFieldName] = useState('');

  useEffect(() => {
    const headerMapping =
      headerMappings && headerMappings.find((mapping) => mapping['header-name'] === header);
    if (headerMapping) {
      setSelectedFieldName(headerMapping['mapped-field']);
    }
  }, [headerMappings]);

  return (
    <TableRow>
      <TableCell>{header}</TableCell>
      <TableCellOverflowHidden>{dataPreview}</TableCellOverflowHidden>
      <TableCell>
        <ReactSelect
          id={`custom-list-report-template-edit__header-mapping-row-select-${rowIndex}`}
          isClearable
          options={mappableFields.map((field) => ({ label: field.name, value: field.name }))}
          value={selectedFieldName}
          onChange={(selected) => {
            setSelectedFieldName(selected);
            // Note: If the clear button is clicked, `selected` will be null
            updateHeaderMappings({ 'header-name': header, 'mapped-field': selected });
            setIsHeaderMappingsValidated(false);
            setHeaderMappingValidationErrors([]);
          }}
        />
      </TableCell>
      <TableCell>
        {selectedFieldName &&
          mappableFields.find((field) => field.name === selectedFieldName)?.type}
      </TableCell>
    </TableRow>
  );
};

const TableCellOverflowHidden = styled(TableCell)`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const SuccessMessage = styled.div`
  width: 100%;
  background-color: ${Color.CoreGreen100};
  padding: ${Spacing[4]};
  margin-bottom: ${Spacing[4]};
`;

const ErrorMessage = styled.div`
  width: 100%;
  background-color: ${Color.CoreSlate50};
  padding: ${Spacing[4]};
  margin-bottom: ${Spacing[4]};
`;

const UnorderedList = styled.ul`
  margin-left: ${Spacing[4]};
`;
